// Desktop sidebar: area accordion + task list + ref link.
// Navigation goes through the hash router — click handlers call navigate()
// rather than calling view functions directly. The router then calls the
// exported view functions (showAreaPage, scrollToTask, showRefPage).

import { areaDomId, taskDomId } from './dom-ids.js';
import { navigate } from '../router.js';
import { state } from '../state.js';

let manifestData = null;
let curAreaDomId = 'pg-a1';
let curTaskElId = null;                   // only the clicked task is highlighted
const openSections = new Set(['pg-a1']);  // multiple areas can be expanded

export function getCurrentAreaId() { return curAreaDomId; }

export function setCurrentTaskElId(id) { curTaskElId = id; }

export function initSidebar(manifest) {
  manifestData = manifest;
  const sb = document.getElementById('sidebar');
  if (!sb) return;
  buildSidebar();
  sb.addEventListener('click', handleClick);
  state.subscribe(buildSidebar);
}

function buildSidebar() {
  const sb = document.getElementById('sidebar');
  if (!sb || !manifestData) return;
  sb.innerHTML = manifestData.areas.map(area => {
    const areaPgId = areaDomId(area.id);
    const isOpen = openSections.has(areaPgId);
    const isActiveArea = areaPgId === curAreaDomId;
    const taskItems = area.tasks.map(t => {
      const tDomId = taskDomId(t.id);
      const viewed = state.tasks.isViewed(t.id);
      return `
      <div class="sb-task${tDomId === curTaskElId ? ' active' : ''}${viewed ? ' viewed' : ''}" data-action="goto-task" data-area-id="${areaPgId}" data-manifest-area-id="${area.id}" data-task-letter="${t.letter}">
        <span class="sb-task-code">${t.letter}</span>
        ${t.title}
      </div>`;
    }).join('');
    return `
      <div class="sb-area${isOpen ? ' open' : ''}" id="sbarea-${areaPgId}">
        <div class="sb-area-hdr${isActiveArea ? ' active-area' : ''}${isOpen ? ' open' : ''}" data-action="toggle-area" data-area-id="${areaPgId}">
          <div class="sb-area-label">
            <span class="sb-area-num">Area ${area.id}</span>
            <span class="sb-area-name">${area.title}</span>
          </div>
          <span class="sb-area-arrow"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"><polyline points="3.5,2 6.5,5 3.5,8"/></svg></span>
        </div>
        <div class="sb-tasks">${taskItems}</div>
      </div>`;
  }).join('') + `
    <div class="sb-ref${curAreaDomId === 'pg-ref' ? ' active' : ''}" data-action="show-ref">
      <span class="sb-ref-icon"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"><line x1="2" y1="3" x2="10" y2="3"/><line x1="2" y1="6" x2="10" y2="6"/><line x1="2" y1="9" x2="10" y2="9"/></svg></span> Numbers & Rules Reference
    </div>`;
}

function handleClick(e) {
  const target = e.target.closest('[data-action]');
  if (!target) return;
  const action = target.dataset.action;
  if (action === 'toggle-area') {
    toggleSbArea(target.dataset.areaId);
  } else if (action === 'goto-task') {
    navigate(`#/area/${target.dataset.manifestAreaId}/task/${target.dataset.taskLetter}`);
  } else if (action === 'show-ref') {
    navigate('#/ref');
  }
}

// Local sidebar open/close — no navigation
function toggleSbArea(areaPgId) {
  if (openSections.has(areaPgId)) openSections.delete(areaPgId);
  else openSections.add(areaPgId);
  buildSidebar();
}

export function showAreaPage(areaPgId) {
  curAreaDomId = areaPgId;
  openSections.add(areaPgId);
  document.querySelectorAll('.study-area, .ref-page').forEach(p => p.classList.remove('active'));
  const pg = document.getElementById(areaPgId);
  if (pg) pg.classList.add('active');
  const c = document.querySelector('.content');
  if (c) c.scrollTop = 0;
  buildSidebar();
}

export function showRefPage() {
  curAreaDomId = 'pg-ref';
  curTaskElId = null;
  document.querySelectorAll('.study-area, .ref-page').forEach(p => p.classList.remove('active'));
  const pg = document.getElementById('pg-ref');
  if (pg) pg.classList.add('active');
  buildSidebar();
}

export function scrollToTask(taskElId) {
  const el = document.getElementById(taskElId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function rebuildSidebar() {
  buildSidebar();
}

// Desktop scroll-spy: as the user scrolls through the active area, update
// the sidebar's highlighted task (and the hash URL silently via
// replaceState, so reload/bookmark reflects what's on screen).
export function initSidebarScrollSpy() {
  const contentEl = document.querySelector('.content');
  if (!contentEl) return;

  function onScroll() {
    if (window.innerWidth < 768) return;
    if (!manifestData) return;
    const activeArea = document.getElementById(curAreaDomId);
    if (!activeArea) return;
    const tasks = activeArea.querySelectorAll('.task-section.task-anchor');
    if (!tasks.length) return;
    let best = null;
    for (const el of tasks) {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.3) best = el;
      else break;
    }
    if (!best) best = tasks[0];
    if (best.id === curTaskElId) return;

    curTaskElId = best.id;
    buildSidebar();

    // Keep URL in sync without polluting history. Translate DOM task id →
    // manifest task id to build the hash.
    const area = manifestData.areas.find(a => areaDomId(a.id) === curAreaDomId);
    if (area) {
      const task = area.tasks.find(t => taskDomId(t.id) === best.id);
      if (task) {
        const newHash = `#/area/${area.id}/task/${task.letter}`;
        if (location.hash !== newHash) {
          history.replaceState(null, '', newHash);
        }
      }
    }
  }

  contentEl.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
}
